import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Textarea, Label } from '@/components/ui'
import { Binary, ArrowLeftRight, Copy, Check, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

type Mode = 'encode' | 'decode'

export function Base64() {
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [mode, setMode] = useState<Mode>('encode')
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState('')

  const processText = (text: string, processMode: Mode) => {
    setError('')
    if (!text) {
      setOutput('')
      return
    }

    try {
      if (processMode === 'encode') {
        const encoded = btoa(unescape(encodeURIComponent(text)))
        setOutput(encoded)
      } else {
        const decoded = decodeURIComponent(escape(atob(text)))
        setOutput(decoded)
      }
    } catch (e) {
      setError(processMode === 'decode' ? 'Invalid Base64 string' : 'Encoding failed')
      setOutput('')
    }
  }

  const handleInputChange = (value: string) => {
    setInput(value)
    processText(value, mode)
  }

  const toggleMode = () => {
    const newMode = mode === 'encode' ? 'decode' : 'encode'
    setMode(newMode)
    // Swap input and output
    const temp = input
    setInput(output)
    setOutput(temp)
    processText(output, newMode)
  }

  const copyOutput = async () => {
    if (!output) return

    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const clearAll = () => {
    setInput('')
    setOutput('')
    setError('')
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Binary className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Base64 Encoder/Decoder</h1>
            <p className="text-muted-foreground">Convert text to and from Base64 encoding</p>
          </div>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className="flex items-center justify-center gap-4">
        <Button
          variant={mode === 'encode' ? 'default' : 'outline'}
          onClick={() => {
            setMode('encode')
            processText(input, 'encode')
          }}
        >
          Encode
        </Button>
        <Button variant="ghost" size="icon" onClick={toggleMode}>
          <ArrowLeftRight className="h-4 w-4" />
        </Button>
        <Button
          variant={mode === 'decode' ? 'default' : 'outline'}
          onClick={() => {
            setMode('decode')
            processText(input, 'decode')
          }}
        >
          Decode
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>{mode === 'encode' ? 'Plain Text' : 'Base64 Input'}</CardTitle>
            <CardDescription>
              {mode === 'encode' ? 'Enter text to encode' : 'Enter Base64 string to decode'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder={mode === 'encode' ? 'Enter plain text...' : 'Enter Base64 string...'}
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              className="min-h-[250px] font-mono"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{input.length} characters</span>
              <Button variant="ghost" size="sm" onClick={clearAll} disabled={!input}>
                <Trash2 className="h-3 w-3 mr-1" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>{mode === 'encode' ? 'Base64 Output' : 'Decoded Text'}</CardTitle>
            <CardDescription>
              {mode === 'encode' ? 'Encoded Base64 string' : 'Decoded plain text'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Textarea
                value={error || output}
                readOnly
                placeholder="Output will appear here..."
                className={`min-h-[250px] font-mono ${error ? 'text-destructive' : ''}`}
              />
              {output && !error && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={copyOutput}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{output.length} characters</span>
              {mode === 'encode' && output && (
                <span>
                  Size increase: {((output.length / input.length - 1) * 100).toFixed(0)}%
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <Label className="text-muted-foreground">What is Base64?</Label>
              <p className="mt-1 text-foreground">
                Base64 is a binary-to-text encoding scheme that represents binary data in an ASCII string format.
                It's commonly used for encoding data in URLs, emails, and storing complex data in text formats.
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">Common Uses</Label>
              <ul className="mt-1 list-disc list-inside text-foreground space-y-1">
                <li>Embedding images in HTML/CSS</li>
                <li>Encoding data for URLs</li>
                <li>Email attachments (MIME)</li>
                <li>Storing binary data in JSON</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
